import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	Output,
} from '@angular/core';
import { makeNamePossessive } from '@core/util/client';
import { PersonClient } from '@models/clients';
import { IdentificationPriority } from '@models/identification';
import { TreefortIdentification } from '@models/idv';
import { cloneDeep } from 'lodash';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { IDLabels } from 'src/app/shared/data/Identification';
import {
	IDSyncData,
	IdvReviewModalData,
} from '../../idv-review-modal.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { formatISO } from 'date-fns';

enum IdSides {
	FRONT = 'Side 1',
	BACK = 'Side 2',
}

enum TreefortUrlKeys {
	FIRST_ID_URL = 'firstIdUrl',
	SECOND_ID_URL = 'secondIdUrl',
}

interface TreefortUrlOptions {
	name: IdSides;
	value: IdSides;
	key: TreefortUrlKeys;
}
@Component({
	selector: 'app-sync-id-data',
	templateUrl: './sync-id-data.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SyncIdDataComponent {
	@SelectSnapshot(BusinessUsersState.getLoggedInUserFullName)
	public loggedInUserFullName: string;
	public client: PersonClient;
	public stepCount: number;
	public buttonText: string;
	public currentTreefortId: IdentificationPriority =
		IdentificationPriority.PRIMARY;
	public treefortId: TreefortIdentification;
	public IDLabels = IDLabels;
	public isSecondId = false;
	public treefortIdUrls: TreefortUrlOptions[] = [
		{
			name: IdSides.FRONT,
			value: IdSides.FRONT,
			key: TreefortUrlKeys.FIRST_ID_URL,
		},
		{
			name: IdSides.BACK,
			value: IdSides.BACK,
			key: TreefortUrlKeys.SECOND_ID_URL,
		},
	];
	public selectedTreefortIdUrl: TreefortUrlOptions = this.treefortIdUrls[0];

	@Input() public syncArray: IDSyncData[];
	@Input() set stepCountValue(stepCount: number) {
		this.stepCount = stepCount;
		this.buttonText = this.stepCount === 2 ? 'Sync ID 1' : 'Sync ID 2';
		this.setTreefortId(stepCount);
		this.selectedTreefortIdUrl = this.treefortIdUrls[0]; // default to front
		this.isSecondId = this.stepCount === 3 && this.multipleTreefortIds();
	}

	@Input() set data(value: IdvReviewModalData) {
		this.client = value.client;
	}

	@Output() public stepCountChange = new EventEmitter<number>();
	@Output() public syncArrayChange = new EventEmitter<IDSyncData[]>();

	constructor(private analytics: AnalyticsService) {}

	getNameText(): string {
		return makeNamePossessive(this.client?.idv?.clientName);
	}

	pushToSyncArray(): void {
		this.syncArrayChange.emit([
			...this.syncArray,
			{
				identification: this.treefortId,
				clientId: this.client?.id,
				overwriteSlot: this.currentTreefortId,
			},
		]);
	}

	confirm() {
		if (this.stepCount === 3) {
			this.analytics.logEvent(AnalyticsEvent.TREEFORT_SYNC_ID);
		}
		this.stepCountChange.emit(this.stepCount + 1);
		this.pushToSyncArray();
		this.setTreefortId(this.stepCount + 1);
	}

	back(): void {
		this.stepCountChange.emit(this.stepCount - 1);
		this.setTreefortId(this.stepCount - 1);
		this.syncArrayChange.emit(this.syncArray.slice(0, -1));
	}

	skip(): void {
		this.stepCountChange.emit(this.stepCount + 1);
		this.setTreefortId(this.stepCount + 1);
	}

	setSelectedTreefortIdUrl(result: DropdownResult<TreefortUrlOptions>) {
		this.selectedTreefortIdUrl = result.result;
		this.treefortId = cloneDeep({
			...this.treefortId,
			latestIdUrl: this.treefortId?.[this.selectedTreefortIdUrl.key],
		});
	}

	private multipleTreefortIds(): boolean {
		return !!this.client?.idv?.identification?.[
			IdentificationPriority.SECONDARY
		];
	}

	private setTreefortId(count: number): void {
		if (count === 2) {
			this.currentTreefortId = IdentificationPriority.PRIMARY;
		} else if (count === 3 && this.multipleTreefortIds()) {
			this.currentTreefortId = IdentificationPriority.SECONDARY;
		}
		const currentTreefortIdentification =
			this.client?.idv?.identification?.[this.currentTreefortId];
		this.treefortId = cloneDeep({
			...currentTreefortIdentification,
			verifiedBy: this.loggedInUserFullName,
			dateOfVerification: formatISO(new Date(), { representation: 'date' }),
			latestIdUrl:
				currentTreefortIdentification?.[this.selectedTreefortIdUrl.key],
		});
	}
}
